/**
 * Wall-clock time in a named zone <-> the unambiguous instant (PROJECT_SPEC.md §12 TIME-006/008/009/011;
 * 21 CFR 11.10(e) "date and time of operator entries", EU Annex 11 §9). A record stores an instant (UTC);
 * a local wall time is only ever an input to be resolved, and it must be resolved without guessing:
 * the hour a DST change skips does not exist, and the hour it repeats exists twice.
 * Pure and browser-safe (Intl only).
 */

export type LocalResolution =
  | { kind: "OK"; utc: string }
  /** DST ended: this wall time happened twice. Both instants are offered; the caller must choose, never default. */
  | { kind: "AMBIGUOUS"; earlier: string; later: string }
  /** DST began: this wall time never happened in this zone, so no record may carry it. */
  | { kind: "SKIPPED"; message: string };

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;
const MS_PER_MIN = 60_000;
const SEARCH_WINDOW_MS = 36 * 3_600_000; // wide enough to see the offset on both sides of any DST change

/** Offset of `tz` from UTC at an instant, in minutes (positive = east of Greenwich). */
export function offsetMinutesAt(utcMs: number, tz: string): number {
  const wholeSecond = Math.floor(utcMs / 1000) * 1000;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(wholeSecond));
  const n = (t: string): number => Number(parts.find((p) => p.type === t)?.value);
  const wallAsUtc = Date.UTC(n("year"), n("month") - 1, n("day"), n("hour"), n("minute"), n("second"));
  return Math.round((wallAsUtc - wholeSecond) / MS_PER_MIN);
}

/** Resolve `YYYY-MM-DDTHH:mm:ss` read on a wall clock in `tz` to the instant(s) it denotes. */
export function resolveLocal(local: string, tz: string): LocalResolution {
  const m = LOCAL.exec(local);
  if (!m) throw new Error(`not a local date-time (YYYY-MM-DDTHH:mm:ss): ${local}`);
  const [y, mo, d, h, mi, s] = m.slice(1).map(Number) as [number, number, number, number, number, number];
  const asIfUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  const offsets = new Set([
    offsetMinutesAt(asIfUtc - SEARCH_WINDOW_MS, tz),
    offsetMinutesAt(asIfUtc + SEARCH_WINDOW_MS, tz),
  ]);
  // a candidate is real only if the zone, asked what the wall clock read at that instant, agrees
  const real = [...offsets]
    .map((off) => asIfUtc - off * MS_PER_MIN)
    .filter((utc) => offsetMinutesAt(utc, tz) * MS_PER_MIN === asIfUtc - utc)
    .sort((a, b) => a - b)
    .map((utc) => new Date(utc).toISOString());
  if (real.length === 1) return { kind: "OK", utc: real[0] as string };
  if (real.length === 2) return { kind: "AMBIGUOUS", earlier: real[0] as string, later: real[1] as string };
  return {
    kind: "SKIPPED",
    message: `${local} never happened in ${tz}: the clocks moved forward over it. Record the time as UTC or pick a time that existed.`,
  };
}

/**
 * An interfaced timestamp (TIME-011): converted from the source system's zone to UTC for ordering, with the
 * source's own reading and zone kept verbatim beside it so the original is never lost. An ambiguous or
 * non-existent source time is not guessed at; the caller gets the problem back.
 */
export interface InterfacedTimestamp {
  sourceLocal: string;
  sourceZone: string;
  utc: string;
}

export function fromSourceZone(
  sourceLocal: string,
  sourceZone: string,
):
  | { ok: true; value: InterfacedTimestamp }
  | { ok: false; resolution: Exclude<LocalResolution, { kind: "OK" }> } {
  const r = resolveLocal(sourceLocal, sourceZone);
  if (r.kind !== "OK") return { ok: false, resolution: r };
  return { ok: true, value: { sourceLocal, sourceZone, utc: r.utc } };
}
